import { ICONS } from '@qlevr/shared/constants';
import { SectionInterface } from '@qlevr/shared/interfaces';
import { useTranslations } from 'next-intl';
import IconRenderer from '../icons/icon-renderer.component';

export interface RatingProps {
  className?: string;
  section?: SectionInterface;
  reviews?: number;
  showText?: boolean;
  averageScore?: number; // Ensure this is always passed
}

export function StarRating({ section, reviews = 0, className = '', showText = true, averageScore = 0 }: RatingProps) {
  const t = useTranslations();

  // Ensure averageScore is a valid number
  const rating = Number.isFinite(averageScore) ? Math.max(0, Math.min(5, averageScore)) : 0;

  // Compute star counts safely
  let fullStars = Math.floor(rating);
  const decimalPart = rating - fullStars;
  let hasHalfStar = false;

  if (decimalPart >= 0.75) {
    fullStars += 1; // Round up to next full star
  } else if (decimalPart >= 0.25) {
    hasHalfStar = true; // Show half star
  }

  // Ensure all values are non-negative and within range
  fullStars = Math.max(0, Math.min(5, fullStars));
  const emptyStars = Math.max(0, 5 - fullStars - (hasHalfStar ? 1 : 0));

  return (
    <div className={`flex gap-2 ${section?.theme.rating} ${className}`}>
      <a href="#review" className="flex items-center gap-2">
        <div className="flex items-center gap-0.5">
          {Array.from({ length: fullStars }, (_, index) => (
            <IconRenderer key={`full-${index}`} icon={ICONS.fullStar} className="h-5 w-5" />
          ))}
          {hasHalfStar && <IconRenderer key="half" icon={ICONS.halfStar} className="h-5 w-5" />}
          {Array.from({ length: emptyStars }, (_, index) => (
            <IconRenderer key={`empty-${index}`} icon={ICONS.emptyStar} className="h-5 w-5" />
          ))}
        </div>
        <span className="text-base font-medium">{showText ? t('review', { reviews: reviews }) : `(${reviews})`}</span>
      </a>
    </div>
  );
}

export default StarRating;
